import React, { CSSProperties, PropsWithChildren, forwardRef } from "react";

import css from "./Container.module.scss";

interface ContainerProps {
	/**
	 * Unique id for container
	 */
	id?: string;
	/**
	 * Padding of the flex box. (Numbers are multiples of 8px.
	 * Ex: 1 = 8px, 2 = 16px, 3 = 24px, etc.)
	 * String can be used to specify padding in any unit.
	 * Ex: "1rem", "1em", "1px", "1vh", "1vw", "100px", "10px 10px 10px 10px", "10px 10px"
	 */
	padding?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | string;
	/**
	 * center vertically and horizontally
	 */
	center?: boolean;
	/**
	 * center horizontally
	 */
	centerHorizontal?: boolean;
	/**
	 * center vertically
	 */
	centerVertical?: boolean;
	/**
	 * width of the container
	 * Ex: 100, "100px", "100%", "100vw"
	 */
	width?: number | string;
	/**
	 * height of the container
	 * Ex: 100, "100px", "100%", "100vh"
	 */
	height?: number | string;
	/**
	 * Additonal classes to add to the grid.
	 */
	className?: string;
	/**
	 * margin of the container (Numbers are multiples of 8px.
	 * Ex: 1 = 8px, 2 = 16px, 3 = 24px, etc. String is the same as css margin property)
	 * Ex: 1, "10px", "10px 10px 10px 10px", "10px 10px"
	 */
	margin?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | string;
	/**
	 * margin left (Numbers are multiples of 8px.
	 * Ex: 1 = 8px, 2 = 16px, 3 = 24px, etc. String is the same as css margin property
	 * Ex: "10px", 10
	 */
	marginLeft?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | string;
	/**
	 * margin right (Numbers are multiples of 8px.
	 * Ex: 1 = 8px, 2 = 16px, 3 = 24px, etc. String is the same as css margin property
	 */
	marginRight?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | string;
	/**
	 * margin top (Numbers are multiples of 8px.
	 * Ex: 1 = 8px, 2 = 16px, 3 = 24px, etc. String is the same as css margin property
	 */
	marginTop?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | string;
	/**
	 * margin bottom (Numbers are multiples of 8px.
	 * Ex: 1 = 8px, 2 = 16px, 3 = 24px, etc. String is the same as css margin property
	 */
	marginBottom?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | string;
	/**
	 * background color of the container
	 */
	background?: string;
	/**
	 * gap between children of the container
	 * Ex: "10px", "10%", "10vw"
	 */
	gap?: string;
	/**
	 * If true, the container will be a flex column
	 */
	column?: boolean;
	/**
	 * If true, the container will be a block
	 */
	block?: boolean;
	/**
	 * Test id for testing
	 */
	testId?: string;
	/* Align items vertically
	 */
	verticalAlignment?: CSSProperties["alignItems"];
	/**
	 * Align items horizontally
	 */
	horizontalAlignment?: CSSProperties["justifyContent"];
	/**
	 * If true, the container will have a border
	 */
	border?: CSSProperties["border"];
	/**
	 * If provided, the container will apply a border radius
	 */
	borderRadius?: CSSProperties["borderRadius"];
	/**
	 * Flex grow value
	 */
	flex?: number;
	/**
	 * If true, the container will wrap its children
	 */
	wrap?: boolean;
}

const Container = forwardRef<HTMLDivElement, PropsWithChildren<ContainerProps>>(
	(props: PropsWithChildren<ContainerProps>, ref) => {
		const {
			id = "",
			children,
			className,
			padding,
			width,
			height,
			center,
			centerHorizontal,
			centerVertical,
			wrap,
			flex,
		} = props;

		const {
			margin,
			marginLeft,
			marginRight,
			marginTop,
			marginBottom,
			background,
			gap,
			column,
			verticalAlignment,
			horizontalAlignment,
			block,
			border,
			borderRadius,
		} = props;

		const containerClasses = [css.container, className || ""].filter(Boolean).join(" ");
		const containerStyles: React.CSSProperties = {};

		if (width) {
			containerStyles.width = typeof width === "number" ? `${width}px` : width;
		}

		if (height) {
			containerStyles.height = typeof height === "number" ? `${height}px` : height;
		}

		if (padding) {
			containerStyles.padding = typeof padding === "number" ? `${padding * 8}px` : padding;
		}

		if (center) {
			containerStyles.justifyContent = "center";
			containerStyles.alignItems = "center";
		}

		if (centerVertical) {
			containerStyles.alignItems = "center";
		}

		if (centerHorizontal) {
			containerStyles.justifyContent = "center";
		}

		if (verticalAlignment) {
			containerStyles.alignItems = verticalAlignment;
		}

		if (horizontalAlignment) {
			containerStyles.justifyContent = horizontalAlignment;
		}

		if (marginLeft) {
			containerStyles.marginLeft = typeof marginLeft === "number" ? `${marginLeft * 8}px` : marginLeft;
		}

		if (marginRight) {
			containerStyles.marginRight = typeof marginRight === "number" ? `${marginRight * 8}px` : marginRight;
		}

		if (marginTop) {
			containerStyles.marginTop = typeof marginTop === "number" ? `${marginTop * 8}px` : marginTop;
		}

		if (marginBottom) {
			containerStyles.marginBottom = typeof marginBottom === "number" ? `${marginBottom * 8}px` : marginBottom;
		}

		if (margin) {
			containerStyles.margin = typeof margin === "number" ? `${margin * 8}px` : margin;
		}

		if (background) {
			containerStyles.background = background;
		}

		if (gap) {
			containerStyles.gap = gap;
		}

		if (column) {
			containerStyles.flexDirection = "column";
		}

		if (block) {
			containerStyles.flex = 1;
			containerStyles.display = "block";
		}

		if (border) {
			containerStyles.border = border;
		}

		if (borderRadius) {
			containerStyles.borderRadius = borderRadius;
		}

		if (wrap) {
			containerStyles.flexWrap = "wrap";
		}

		if (flex) {
			containerStyles.flex = flex;
		}

		return (
			<div id={id} className={containerClasses} style={containerStyles} data-testid={props.testId} ref={ref}>
				{children}
			</div>
		);
	}
);

Container.displayName = "Container";

export default Container;
